import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Upload, Download, Trash2, Loader2, Mail, ListFilter, Pencil, Save, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const TeilnehmerTab = ({ onStatsUpdate }) => {
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [kurse, setKurse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [csvFile, setCsvFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [groupByCourse, setGroupByCourse] = useState(false);

  // Edit state
  const [editingTeilnehmer, setEditingTeilnehmer] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTeilnehmer();
    fetchKurse();
  }, []);

  const fetchTeilnehmer = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/teilnehmer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeilnehmer(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Teilnehmer');
    } finally {
      setLoading(false);
    }
  };

  const fetchKurse = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/kurse`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKurse(response.data);
    } catch (error) {
      console.error('Error fetching kurse:', error);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFile(file);
    setUploading(true);

    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/teilnehmer/preview`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setPreview(response.data);
      toast.success('Vorschau geladen');
    } catch (error) {
      toast.error('Fehler beim Laden der Vorschau');
      setCsvFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleImport = async () => {
    if (!csvFile) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await axios.post(`${API}/admin/teilnehmer/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`${response.data.imported_count} Teilnehmer importiert`);
      setCsvFile(null);
      setPreview(null);
      fetchTeilnehmer();
      onStatsUpdate();
    } catch (error) {
      toast.error('Fehler beim Importieren');
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/teilnehmer/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 16).replace('T', '_').replace(/:/g, '-');
      link.setAttribute('download', `teilnehmer_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('CSV exportiert');
    } catch (error) {
      toast.error('Fehler beim Exportieren');
    }
  };

  const handleDeleteAll = async () => {
    if (deleteConfirmText !== 'LÖSCHEN') {
      toast.error('Bitte gib "LÖSCHEN" ein');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/teilnehmer`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Alle Teilnehmer gelöscht');
      setDeleteConfirmText('');
      fetchTeilnehmer();
      onStatsUpdate();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleDeleteSingle = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/teilnehmer/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Teilnehmer gelöscht');
      fetchTeilnehmer();
      onStatsUpdate();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Edit functions
  const handleEdit = (t) => {
    setEditingTeilnehmer(t);
    setEditFormData({
      vorname: t.vorname || '',
      nachname: t.nachname || '',
      geburtsdatum: t.geburtsdatum || '',
      email: t.email || '',
      telefon: t.telefon || '',
      theoriekurs: t.theoriekurs || '',
      pruefung_stufe: t.pruefung_stufe || '',
      pruefung_punkte: t.pruefung_punkte || '',
      pruefung_note: t.pruefung_note || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingTeilnehmer) return;
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/teilnehmer/${editingTeilnehmer.id}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Teilnehmer aktualisiert');
      setEditingTeilnehmer(null);
      fetchTeilnehmer();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const getSortedTeilnehmer = () => {
    const sorted = [...teilnehmer].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return `${a.nachname} ${a.vorname}`.localeCompare(`${b.nachname} ${b.vorname}`);
        case 'kurs':
          return (a.theoriekurs || '').localeCompare(b.theoriekurs || '');
        case 'note':
          return (a.pruefung_note || '').localeCompare(b.pruefung_note || '');
        default:
          return 0;
      }
    });
    return sorted;
  };

  const getGroupedTeilnehmer = () => {
    const sorted = getSortedTeilnehmer();
    if (!groupByCourse) return { 'Alle': sorted };

    const grouped = {};
    sorted.forEach(tn => {
      const kurs = tn.theoriekurs || 'Kein Kurs';
      if (!grouped[kurs]) grouped[kurs] = [];
      grouped[kurs].push(tn);
    });
    return grouped;
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = teilnehmer.map(tn => tn.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
    }
  };

  const isSelected = (id) => selectedIds.includes(id);
  const allSelected = teilnehmer.length > 0 && selectedIds.length === teilnehmer.length;

  const handleSendEmail = () => {
    const selectedTeilnehmer = teilnehmer.filter(tn => selectedIds.includes(tn.id));
    const emails = selectedTeilnehmer
      .map(tn => tn.email)
      .filter(email => email && email.trim() !== '');

    if (emails.length === 0) {
      toast.error('Keine E-Mail-Adressen bei den ausgewählten Teilnehmern gefunden');
      return;
    }

    const mailtoLink = `mailto:?bcc=${emails.join(',')}`;
    window.location.href = mailtoLink;
    
    toast.success(`E-Mail-Client geöffnet mit ${emails.length} Empfänger(n) in BCC`);
  };

  return (
    <div className="space-y-6">
      {/* Edit Dialog */}
      <Dialog open={!!editingTeilnehmer} onOpenChange={(open) => !open && setEditingTeilnehmer(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Teilnehmer bearbeiten</DialogTitle>
            <DialogDescription>
              {editingTeilnehmer?.vorname} {editingTeilnehmer?.nachname}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Persönliche Daten */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-gray-700 border-b pb-1">Persönliche Daten</h3>
              <div>
                <Label>Vorname</Label>
                <Input
                  value={editFormData.vorname}
                  onChange={(e) => setEditFormData({...editFormData, vorname: e.target.value})}
                />
              </div>
              <div>
                <Label>Nachname</Label>
                <Input
                  value={editFormData.nachname}
                  onChange={(e) => setEditFormData({...editFormData, nachname: e.target.value})}
                />
              </div>
              <div>
                <Label>Geburtsdatum</Label>
                <Input
                  value={editFormData.geburtsdatum}
                  onChange={(e) => setEditFormData({...editFormData, geburtsdatum: e.target.value})}
                  placeholder="z.B. 15.03.2010"
                />
              </div>
              <div>
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input
                  value={editFormData.telefon}
                  onChange={(e) => setEditFormData({...editFormData, telefon: e.target.value})}
                />
              </div>
            </div>

            {/* Kurs & Prüfung */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-gray-700 border-b pb-1">Kurs & Prüfung</h3>
              <div>
                <Label>Kurs</Label>
                <Select 
                  value={editFormData.theoriekurs || "_none"} 
                  onValueChange={(value) => setEditFormData({...editFormData, theoriekurs: value === "_none" ? "" : value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Kurs auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Kein Kurs</SelectItem>
                    {kurse.map(k => (
                      <SelectItem key={k.id} value={k.kurs}>{k.kurs}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prüfung: Stufe</Label>
                <Input
                  value={editFormData.pruefung_stufe}
                  onChange={(e) => setEditFormData({...editFormData, pruefung_stufe: e.target.value})}
                  placeholder="z.B. Junior, Bronze, Silber, Gold"
                />
              </div>
              <div>
                <Label>Prüfung: Punkte</Label>
                <Input
                  value={editFormData.pruefung_punkte}
                  onChange={(e) => setEditFormData({...editFormData, pruefung_punkte: e.target.value})}
                  placeholder="z.B. 85"
                />
              </div>
              <div>
                <Label>Prüfung: Note</Label>
                <Input
                  value={editFormData.pruefung_note}
                  onChange={(e) => setEditFormData({...editFormData, pruefung_note: e.target.value})}
                  placeholder="z.B. Sehr Gut, Gut, Bestanden"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTeilnehmer(null)}>
              <X className="h-4 w-4 mr-2" />
              Abbrechen
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>CSV importieren</CardTitle>
          <CardDescription>
            Lade eine CSV-Datei hoch, um Teilnehmerdaten zu importieren. Der Import überschreibt alle vorhandenen Daten.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="csv-file" className="cursor-pointer">
              <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-blue-400 transition-colors" style={{ borderColor: '#cbd5e0' }}>
                <Upload className="mx-auto h-12 w-12 mb-2 text-blue-600" />
                <p className="font-medium text-gray-900">CSV-Datei auswählen</p>
                <p className="text-sm mt-1 text-gray-600">oder Datei hierher ziehen</p>
                {csvFile && <p className="text-sm mt-2 font-medium text-blue-600">{csvFile.name}</p>}
              </div>
            </Label>
            <Input
              id="csv-file"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleFileSelect}
              data-testid="csv-upload-input"
            />
          </div>

          {preview && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-amber-50 border-l-4 border-amber-400">
                <p className="font-medium text-amber-800">Vorschau: {preview.total_rows} Zeilen</p>
                {preview.duplicates_warning && (
                  <p className="text-sm mt-1 text-amber-700">{preview.duplicates_warning}</p>
                )}
              </div>

              <div className="overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {preview.headers.slice(0, 8).map((header, idx) => (
                        <TableHead key={idx}>{header}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.map((row, idx) => (
                      <TableRow key={idx}>
                        {row.slice(0, 8).map((cell, cellIdx) => (
                          <TableCell key={cellIdx}>{cell}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleImport}
                  disabled={uploading}
                  data-testid="confirm-import-button"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Importiere...
                    </>
                  ) : (
                    'Import bestätigen'
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setCsvFile(null);
                    setPreview(null);
                  }}
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Aktionen</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleExport}
            data-testid="export-csv-button"
            className="flex items-center gap-2"
            disabled={teilnehmer.length === 0}
          >
            <Download className="h-4 w-4" />
            CSV exportieren
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                data-testid="delete-all-button"
                className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                disabled={teilnehmer.length === 0}
              >
                <Trash2 className="h-4 w-4" />
                Alle löschen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Alle Teilnehmer löschen?</AlertDialogTitle>
                <AlertDialogDescription className="space-y-4">
                  <p>Diese Aktion kann nicht rückgängig gemacht werden. Alle {teilnehmer.length} Teilnehmer werden permanent gelöscht.</p>
                  <div>
                    <Label>Bestätigung: Gib &quot;LÖSCHEN&quot; ein</Label>
                    <Input
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="LÖSCHEN"
                      data-testid="delete-confirm-input"
                      className="mt-2"
                    />
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setDeleteConfirmText('')}>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAll}
                  data-testid="delete-confirm-button"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Löschen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>

      {/* Teilnehmer List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Teilnehmer ({teilnehmer.length})</CardTitle>
            {selectedIds.length > 0 && (
              <Button 
                onClick={handleSendEmail}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Mail className="h-4 w-4 mr-2" />
                E-Mail an ausgewählte ({selectedIds.length})
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
            </div>
          ) : teilnehmer.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              Keine Teilnehmer vorhanden
            </div>
          ) : (
            <>
              {/* Filter und Sortier-Optionen */}
              <div className="flex items-center gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <ListFilter className="h-4 w-4 text-gray-500" />
                  <Label className="text-sm font-medium">Sortieren nach:</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Name</SelectItem>
                      <SelectItem value="kurs">Kurs</SelectItem>
                      <SelectItem value="note">Note</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={groupByCourse}
                    onCheckedChange={setGroupByCourse}
                    id="group-by-course"
                  />
                  <Label htmlFor="group-by-course" className="text-sm cursor-pointer">
                    Nach Kurs gruppieren
                  </Label>
                </div>
              </div>

              {/* Tabelle */}
              <div className="overflow-x-auto">
                {Object.entries(getGroupedTeilnehmer()).map(([groupName, groupTeilnehmer]) => (
                  <div key={groupName} className="mb-6">
                    {groupByCourse && (
                      <h3 className="font-semibold text-lg mb-3 text-gray-900">
                        {groupName} ({groupTeilnehmer.length})
                      </h3>
                    )}
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            {!groupByCourse && groupName === 'Alle' && (
                              <Checkbox
                                checked={allSelected}
                                onCheckedChange={handleSelectAll}
                              />
                            )}
                          </TableHead>
                          <TableHead>Vorname</TableHead>
                          <TableHead>Nachname</TableHead>
                          <TableHead>Geburtsdatum</TableHead>
                          <TableHead>Kurs</TableHead>
                          <TableHead>Note</TableHead>
                          <TableHead className="w-24">Aktionen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {groupTeilnehmer.map((t) => (
                          <TableRow key={t.id}>
                            <TableCell>
                              <Checkbox
                                checked={isSelected(t.id)}
                                onCheckedChange={(checked) => handleSelectOne(t.id, checked)}
                                disabled={!t.email || t.email.trim() === ''}
                              />
                            </TableCell>
                            <TableCell>{t.vorname}</TableCell>
                            <TableCell>{t.nachname}</TableCell>
                            <TableCell>{t.geburtsdatum || '-'}</TableCell>
                            <TableCell>{t.theoriekurs || '-'}</TableCell>
                            <TableCell>{t.pruefung_note || '-'}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(t)}
                                  className="h-8 w-8 p-0 hover:bg-blue-50"
                                  data-testid={`edit-teilnehmer-${t.id}`}
                                >
                                  <Pencil className="h-4 w-4 text-blue-600" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 hover:bg-red-50"
                                      data-testid={`delete-teilnehmer-${t.id}`}
                                    >
                                      <Trash2 className="h-4 w-4 text-red-600" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Teilnehmer löschen?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Möchtest du {t.vorname} {t.nachname} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDeleteSingle(t.id)}
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        Löschen
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TeilnehmerTab;
